/** Balance de 8 columnas (DISENO_CONTABILIDAD §7.2). */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";

import {
  Cargando,
  ErrorCarga,
  PageHeader,
  Select,
  TableWrap,
} from "../../components/ui";
import { hasPermission } from "../../lib/auth";
import { pesos } from "../../lib/format";
import { useAuth } from "../auth/useAuth";
import { fetchBalance, type FilaBalance } from "./api";
import {
  AvisoProvisorio,
  BarraPeriodo,
  BotonesExportar,
  SinDatos,
  usePeriodoUrl,
} from "./componentes";

const COLUMNAS: { clave: keyof FilaBalance; titulo: string }[] = [
  { clave: "debitos", titulo: "Débitos" },
  { clave: "creditos", titulo: "Créditos" },
  { clave: "saldo_deudor", titulo: "Deudor" },
  { clave: "saldo_acreedor", titulo: "Acreedor" },
  { clave: "activo", titulo: "Activo" },
  { clave: "pasivo", titulo: "Pasivo" },
  { clave: "perdida", titulo: "Pérdida" },
  { clave: "ganancia", titulo: "Ganancia" },
];

// Preferencia de vista (sólo UI): mostrar u ocultar la columna Código.
const CLAVE_CODIGO = "innovasuite.contabilidad.balance.codigo";

function leerMostrarCodigo(): boolean {
  try {
    return localStorage.getItem(CLAVE_CODIGO) !== "0";
  } catch {
    return true;
  }
}

function guardarMostrarCodigo(v: boolean): void {
  try {
    localStorage.setItem(CLAVE_CODIGO, v ? "1" : "0");
  } catch {
    // modo privado: la preferencia dura hasta recargar
  }
}

function celda(v: number): string {
  return v === 0 ? "—" : pesos(v);
}

export function BalancePage(): JSX.Element {
  const { state } = useAuth();
  const url = usePeriodoUrl();
  const { search } = useLocation();
  const [nivel, setNivel] = useState(5);
  const [mostrarCodigo, setMostrarCodigo] = useState(leerMostrarCodigo);
  const q = useQuery({
    queryKey: ["contabilidad", "balance", url.filtro, nivel],
    queryFn: () => fetchBalance(url.filtro, nivel),
    // Al cambiar el período se mantiene lo anterior en pantalla mientras carga.
    placeholderData: keepPreviousData,
  });
  const b = q.data;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="space-y-3">
        <PageHeader
          title="Balance 8 columnas"
          actions={
            b &&
            !b.periodo.vacio && (
              <BotonesExportar recurso="balance-8-columnas" params={{ ...url.filtro, nivel }} />
            )
          }
        />
        <div className="flex flex-wrap items-end gap-3">
          <BarraPeriodo periodo={b?.periodo} url={url} />
          <div className="w-44">
            <Select label="Detalle" value={String(nivel)} onChange={(e) => setNivel(Number(e.target.value))}>
              <option value="5">Cuenta</option>
              <option value="4">Nivel 4</option>
              <option value="3">Nivel 3</option>
              <option value="2">Nivel 2</option>
              <option value="1">Nivel 1</option>
            </Select>
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={mostrarCodigo}
              onChange={(e) => {
                setMostrarCodigo(e.target.checked);
                guardarMostrarCodigo(e.target.checked);
              }}
              className="rounded border-neutral-450 text-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500"
            />
            Mostrar código
          </label>
        </div>
        <AvisoProvisorio periodo={b?.periodo} />
      </div>

      {q.isLoading && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {b && b.periodo.vacio && <SinDatos puedeCargar={hasPermission(state, "contabilidad.cargar")} />}

      {b && !b.periodo.vacio && (
        <TableWrap label="Balance 8 columnas">
          <table className="w-full min-w-[1100px] text-sm">
            <caption className="sr-only">Balance de 8 columnas</caption>
            <thead className="sticky top-0 bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
              <tr>
                {mostrarCodigo && (
                  <th rowSpan={2} className="px-3 py-2 text-left font-medium">Código</th>
                )}
                <th rowSpan={2} className="px-3 py-2 text-left font-medium">Cuenta</th>
                <th colSpan={2} className="px-3 pt-2 text-center font-medium">Sumas</th>
                <th colSpan={2} className="px-3 pt-2 text-center font-medium">Saldos</th>
                <th colSpan={2} className="px-3 pt-2 text-center font-medium">Inventario</th>
                <th colSpan={2} className="px-3 pt-2 text-center font-medium">Resultado</th>
              </tr>
              <tr>
                {COLUMNAS.map((c) => (
                  <th key={c.clave} className="px-3 pb-2 text-right font-medium">{c.titulo}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {b.filas.map((f) => (
                <tr key={f.codigo} className="hover:bg-neutral-50">
                  {mostrarCodigo && (
                    <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-neutral-600">{f.codigo}</td>
                  )}
                  <td className="px-3 py-1.5">
                    {f.cuenta_id ? (
                      <Link
                        to={`/contabilidad/mayor/${f.cuenta_id}${search}`}
                        className="text-neutral-900 hover:text-brand-700 hover:underline"
                      >
                        {f.nombre}
                      </Link>
                    ) : f.nombre !== f.codigo ? (
                      <span className="font-medium text-neutral-900">{f.nombre}</span>
                    ) : mostrarCodigo ? (
                      // Grupo sin nombre: se nombra en Plan de cuentas → Grupos.
                      <span className="text-neutral-500">Sin nombre</span>
                    ) : (
                      <span className="font-medium tabular-nums text-neutral-900">{f.codigo}</span>
                    )}
                  </td>
                  {COLUMNAS.map((c) => (
                    <td key={c.clave} className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                      {celda(f[c.clave] as number)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-neutral-300 font-medium">
              {[b.sumas, b.resultado, b.totales].map((f) => (
                <tr key={f.nombre} className={f === b.totales ? "bg-neutral-100" : ""}>
                  {mostrarCodigo && <td />}
                  <td className="px-3 py-1.5 text-neutral-900">{f.nombre}</td>
                  {COLUMNAS.map((c) => (
                    <td key={c.clave} className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                      {celda(f[c.clave] as number)}
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
          <p
            className={`flex items-center gap-1.5 px-3 py-2 text-sm ${b.cuadra ? "text-emerald-700" : "text-rose-700"}`}
          >
            {b.cuadra ? (
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            ) : (
              <XCircle className="h-4 w-4" aria-hidden="true" />
            )}
            {b.cuadra
              ? `Cuadra. ${b.utilidad >= 0 ? "Utilidad" : "Pérdida"} del período: ${pesos(Math.abs(b.utilidad))}.`
              : "No cuadra: revisa el libro diario cargado."}
          </p>
        </TableWrap>
      )}
    </div>
  );
}
